/**
 * PowerDetailModal Component
 * Displays detailed information about a single power
 */

import { Fragment, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild, Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import usePermissions from '../../../../hooks/usePermissions';
import {
  XMarkIcon,
  DocumentTextIcon,
  UserIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
  DocumentIcon,
  PrinterIcon,
  ScaleIcon,
  ShieldExclamationIcon,
  ArrowUturnLeftIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  NoSymbolIcon,
  DocumentMinusIcon,
} from '@heroicons/react/24/outline';
import Tooltip from '@mui/material/Tooltip';
import { OathSpinner, OathConfirmDialog } from '../../../shared/OathUI';
import { getPowerStatusColor } from '../../../shared/OathTableComponents';
import { getPowerDocuments, downloadDocumentPdf } from '../../../../services/oathPowerDocumentService';
import { getPowerRewriteHistory } from '../../../../services/oathPowerReportService';
import DocumentUploadModal from './DocumentUploadModal';
import { validateSetAside } from '../../../../services/oathPowerActionService';
import SetAsideModal from './SetAsideModal';
import DischargeModal from './DischargeModal';
import CommentsSection from './CommentsSection';

/**
 * Format currency value
 */
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

/**
 * Format date value
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

/**
 * Document types for power documents
 */
const DOCUMENT_TYPES = [
  { id: 1, name: "Court Doc's" },
  { id: 2, name: 'Forfeitures' },
  { id: 3, name: 'Judgements / Demands' },
  { id: 4, name: 'Discharges' },
  { id: 5, name: 'Set Aside' },
];

/**
 * Detail field component
 */
const DetailField = ({ label, value, mono = false }) => (
  <div>
    <dt className="text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</dt>
    <dd className={`mt-0.5 sm:mt-1 text-sm text-gray-900 dark:text-white ${mono ? 'font-mono' : ''}`}>
      {value || '-'}
    </dd>
  </div>
);

/**
 * Section component
 */
const Section = ({ icon: Icon, title, children }) => (
  <div className="space-y-2 sm:space-y-3">
    <div className="flex items-center gap-2 pb-1.5 border-b border-gray-200 dark:border-gray-700">
      <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary dark:text-primary-light" />
      <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{title}</h4>
    </div>
    <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
      {children}
    </dl>
  </div>
);

/**
 * PDF Viewer Modal — portaled to body, no React Router interaction.
 * URL is updated silently via window.history.replaceState by the caller.
 */
const PdfViewerModal = ({ isOpen, onClose, powerNo, doc }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (!doc || !isOpen) return;

    let cancelled = false;
    let objectUrl = null;

    const fetchPdf = async () => {
      setIsLoading(true);
      setError(null);
      setPdfUrl(null);

      const result = await downloadDocumentPdf(powerNo, doc.name);

      if (cancelled) return;

      if (result.success && result.data) {
        objectUrl = URL.createObjectURL(result.data);
        setPdfUrl(objectUrl);
      } else {
        setError(result.error || 'Failed to load document');
      }
      setIsLoading(false);
    };

    fetchPdf();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc?.name, isOpen, powerNo]);

  useEffect(() => {
    if (!isOpen && pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }, [isOpen, pdfUrl]);

  if (!doc) return null;

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-6">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="relative w-full max-w-5xl h-[85vh] flex flex-col bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {doc.label}
                </span>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* PDF Container */}
              <div className="flex-1 relative bg-gray-100 dark:bg-gray-950">
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <OathSpinner size="lg" />
                  </div>
                )}

                {error && !isLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                    <ExclamationTriangleIcon className="h-10 w-10" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                {pdfUrl && !error && (
                  <iframe
                    src={pdfUrl}
                    title={doc.label}
                    className="w-full h-full border-0"
                  />
                )}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

/**
 * Document Card Component - clickable card for a document
 * Shows document label, page count, and opens viewer on click
 */
const DocumentCard = ({ doc, onView }) => (
  <button
    onClick={() => onView(doc)}
    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary/50 dark:hover:border-primary/50 hover:shadow-md transition-all text-left group"
  >
    {/* Document icon */}
    <div className="flex-shrink-0 w-9 h-9 bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-lg flex items-center justify-center">
      <DocumentIcon className="h-4 w-4 text-primary dark:text-primary-light" />
    </div>

    {/* Document info */}
    <div className="flex-1 min-w-0">
      <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary-light transition-colors">
        {doc.label}
      </span>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {doc.pages.length} {doc.pages.length === 1 ? 'page' : 'pages'}
        {doc.dateCreated && <> · <span title="Date Received">{formatDate(doc.dateCreated)}</span></>}
      </p>
    </div>

    {/* View indicator */}
    <div className="flex items-center gap-1 flex-shrink-0">
      <span className="text-xs text-gray-400 group-hover:text-primary dark:group-hover:text-primary-light transition-colors">
        View
      </span>
      <EyeIcon className="h-4 w-4 text-gray-400 group-hover:text-primary dark:group-hover:text-primary-light transition-colors" />
    </div>
  </button>
);

/**
 * Documents Section Component - Fetches and displays documents from API
 * Shows document cards grouped by type - click to open viewer and page through
 */
const DocumentsSection = ({ powerNo, initialDocId }) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedType, setSelectedType] = useState(null); // null = all types

  // PDF viewer modal state — initialized from URL doc param if present
  const [viewerDocId, setViewerDocId] = useState(initialDocId || null);

  // Upload modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Fetch documents function (extracted for reuse after upload)
  const fetchDocuments = async () => {
    if (!powerNo) return;

    setIsLoading(true);
    setError(null);

    const result = await getPowerDocuments(powerNo);

    if (result.success) {
      setDocuments(result.data || []);
    } else {
      setError(result.error || 'Failed to load documents');
    }

    setIsLoading(false);
  };

  // Fetch documents when powerNo changes
  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [powerNo]);

  // Handle upload success - refresh document list
  const handleUploadSuccess = () => {
    fetchDocuments();
  };

  // Count documents per type for numbering (e.g., "Forfeiture Doc #1", "Forfeiture Doc #2")
  const docCountByType = documents.reduce((acc, doc) => {
    acc[doc.type] = (acc[doc.type] || 0) + 1;
    return acc;
  }, {});

  // Process documents with labels and attach pages with docName for viewer
  const typeIndexTracker = {};
  const processedDocs = documents.map(doc => {
    typeIndexTracker[doc.type] = (typeIndexTracker[doc.type] || 0) + 1;
    const docIndex = typeIndexTracker[doc.type];
    const totalOfType = docCountByType[doc.type];

    // Singularize type name for label (e.g., "Forfeitures" -> "Forfeiture")
    // Special case: "Judgements / Demands" displays as "Judgment"
    let singularType = doc.type;
    if (singularType === 'Judgements / Demands') {
      singularType = 'Judgment';
    } else if (singularType.endsWith('ies')) {
      singularType = singularType.slice(0, -3) + 'y';
    } else if (singularType.endsWith('s') && !singularType.endsWith("'s")) {
      singularType = singularType.slice(0, -1);
    }

    // Generate label: "Forfeiture Doc #1", "Discharge Doc #2", etc. (always numbered)
    const label = `${singularType} Doc #${docIndex}`;

    // Attach docName and label to each page for the viewer
    const pagesWithMeta = doc.pages.map(page => ({
      ...page,
      docName: doc.name,
      docType: doc.type,
      docLabel: label,
    }));

    return {
      ...doc,
      label,
      docIndex,
      pages: pagesWithMeta,
    };
  });

  // Group documents by type
  const docsByType = processedDocs.reduce((acc, doc) => {
    if (!acc[doc.type]) {
      acc[doc.type] = [];
    }
    acc[doc.type].push(doc);
    return acc;
  }, {});

  // Get unique types
  const availableTypes = Object.keys(docsByType);

  // Get display name for type (for filter pills)
  const getTypeDisplayName = (type) => {
    if (type === 'Judgements / Demands') return 'Judgments';
    return type;
  };

  // Filter documents by selected type
  const displayedDocs = selectedType
    ? processedDocs.filter(d => d.type === selectedType)
    : processedDocs;

  const viewerDoc = viewerDocId ? processedDocs.find(d => d.name === viewerDocId) : null;

  // Silently update doc param in URL without triggering React Router re-renders
  const updateDocInUrl = useCallback((docId) => {
    const url = new URL(window.location.href);
    if (docId) {
      url.searchParams.set('doc', docId);
    } else {
      url.searchParams.delete('doc');
    }
    window.history.replaceState(null, '', url.toString());
  }, []);

  // Handle viewing a document — open modal + update URL
  const handleViewDocument = (doc) => {
    setViewerDocId(doc.name);
    updateDocInUrl(doc.name);
  };

  // Handle closing the viewer — close modal + remove from URL
  const handleCloseViewer = () => {
    setViewerDocId(null);
    updateDocInUrl(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <OathSpinner size="md" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading documents...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <ExclamationTriangleIcon className="h-10 w-10 text-red-400" />
        <p className="mt-3 text-sm text-red-500 dark:text-red-400">{error}</p>
        <button
          onClick={() => {
            setIsLoading(true);
            setError(null);
            getPowerDocuments(powerNo).then(result => {
              setDocuments(result.success ? result.data : []);
              setError(result.success ? null : result.error);
              setIsLoading(false);
            });
          }}
          className="mt-4 px-4 py-2 text-sm text-primary dark:text-primary-light border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  // Empty state
  if (processedDocs.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <DocumentIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">No documents</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 mb-4">
            No documents have been uploaded for this power
          </p>
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg shadow-sm transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Upload Document
          </button>
        </div>
        <DocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          powerNo={powerNo}
          onUploadSuccess={handleUploadSuccess}
        />
      </>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with count and upload button */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <PaperClipIcon className="h-5 w-5 text-primary dark:text-primary-light" />
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {processedDocs.length} {processedDocs.length === 1 ? 'Document' : 'Documents'}
          </span>
        </div>
        <button
          onClick={() => setIsUploadModalOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg shadow-sm transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Upload</span>
        </button>
      </div>

      {/* Type filter pills */}
      {availableTypes.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedType(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              selectedType === null
                ? 'bg-primary text-white shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            All ({processedDocs.length})
          </button>
          {availableTypes.map(type => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                selectedType === type
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {getTypeDisplayName(type)} ({docsByType[type].length})
            </button>
          ))}
        </div>
      )}

      {/* Document List - scrollable for long lists */}
      <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1 -mr-1">
        {displayedDocs.map((doc) => (
          <DocumentCard
            key={doc.name}
            doc={doc}
            onView={handleViewDocument}
          />
        ))}
      </div>

      {/* PDF Viewer Modal — portaled to body so it layers above the power detail dialog */}
      {viewerDocId && createPortal(
        <PdfViewerModal
          isOpen={!!viewerDocId}
          onClose={handleCloseViewer}
          powerNo={powerNo}
          doc={viewerDoc}
        />,
        document.body
      )}

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        powerNo={powerNo}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
};

/**
 * Forfeiture Section Component
 */
const ForfeitureSection = ({ forfeiture }) => {
  if (!forfeiture) return null;

  return (
    <Section icon={ShieldExclamationIcon} title="Forfeiture">
      <DetailField label="Forfeiture Amount" value={formatCurrency(forfeiture.forfeitureAmount)} />
      <DetailField label="Forfeiture Date" value={formatDate(forfeiture.forfeitureDate)} />
      <DetailField label="Due Date" value={formatDate(forfeiture.dueDate)} />
      <DetailField label="Extension Date" value={formatDate(forfeiture.extensionDate)} />
      {forfeiture.judgementDate && (
        <>
          <DetailField label="Judgment Date" value={formatDate(forfeiture.judgementDate)} />
          <DetailField label="Judgment Amount" value={formatCurrency(forfeiture.judgementAmount)} />
        </>
      )}
      {forfeiture.setAsideDate && (
        <DetailField label="Set Aside Date" value={formatDate(forfeiture.setAsideDate)} />
      )}
      {forfeiture.dischargeDate && (
        <DetailField label="Discharge Date" value={formatDate(forfeiture.dischargeDate)} />
      )}
    </Section>
  );
};

/**
 * Judgment Section Component
 */
const JudgmentSection = ({ judgment }) => {
  if (!judgment) return null;

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
        <ScaleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary dark:text-primary-light" />
        <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">Judgment</h4>
        {judgment.dischargeDate && (
          <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-[10px] font-medium rounded">
            Discharged
          </span>
        )}
      </div>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <DetailField label="Judgment Amount" value={formatCurrency(judgment.judgmentAmount)} />
        <DetailField label="Judgment Date" value={formatDate(judgment.judgmentDate)} />
        <DetailField label="Due Date" value={formatDate(judgment.judgmentDueDate)} />
        {judgment.duePayableAmount && (
          <DetailField label="Due Payable Amount" value={formatCurrency(judgment.duePayableAmount)} />
        )}
        {judgment.duePayableDate && (
          <DetailField label="Due Payable Date" value={formatDate(judgment.duePayableDate)} />
        )}
        {judgment.dischargeDate && (
          <DetailField label="Discharge Date" value={formatDate(judgment.dischargeDate)} />
        )}
      </dl>
    </div>
  );
};

/**
 * Set-Aside Section Component
 */
const SetAsideSection = ({ setAside }) => {
  if (!setAside) return null;

  return (
    <Section icon={ArrowUturnLeftIcon} title="Set-Aside">
      <DetailField label="Set-Aside Date" value={formatDate(setAside.setAsideDate)} />
      <DetailField label="Reason" value={setAside.setAsideReasonDescription || setAside.setAsideReasonCd} />
      {setAside.setAsideReasonOther && (
        <DetailField label="Other Reason" value={setAside.setAsideReasonOther} />
      )}
    </Section>
  );
};

/**
 * Rewrite History Section Component
 * Fetches and displays rewrite history for a power
 */
const RewriteHistorySection = ({ power }) => {
  const [history, setHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!power?.powerNo) return;

    let cancelled = false;

    const fetchHistory = async () => {
      setIsLoading(true);
      setError(null);

      const result = await getPowerRewriteHistory(power.powerNo);

      if (cancelled) return;

      if (result.success) {
        setHistory(result.data);
      } else {
        setError(result.error || 'Failed to load rewrite history');
      }

      setIsLoading(false);
    };

    fetchHistory();

    return () => { cancelled = true; };
  }, [power?.powerNo]);

  // Determine if this power is the original (from) or the rewrite (to)
  // Both fields are populated on the record - compare against powerNo to determine role
  const isOriginal = power.rewriteFromPowerNo === power.powerNo;
  const isRewrite = power.rewritePowerNo === power.powerNo;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Rewrite Role Banner */}
      <div className={`flex items-center gap-3 p-3 sm:p-4 rounded-lg border ${
        isOriginal
          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
          : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
      }`}>
        <ArrowUturnLeftIcon className={`h-5 w-5 flex-shrink-0 ${
          isOriginal ? 'text-blue-600 dark:text-blue-400' : 'text-primary dark:text-primary-light'
        }`} />
        <div>
          <p className={`text-sm font-semibold ${
            isOriginal ? 'text-blue-700 dark:text-blue-300' : 'text-primary dark:text-primary-light'
          }`}>
            {isOriginal ? 'This power was rewritten' : 'This power is a rewrite'}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
            {isOriginal
              ? <>Replaced by power <span className="font-mono font-semibold">{power.rewritePowerNo}</span></>
              : <>Replaces original power <span className="font-mono font-semibold">{power.rewriteFromPowerNo}</span></>
            }
          </p>
        </div>
      </div>

      {/* Quick Reference */}
      <Section icon={DocumentTextIcon} title="Rewrite Details">
        <DetailField label="Original Power" value={isOriginal ? power.powerNo : power.rewriteFromPowerNo} mono />
        <DetailField label="Rewrite Power" value={isOriginal ? power.rewritePowerNo : power.powerNo} mono />
      </Section>

      {/* API-fetched History */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-8">
          <OathSpinner size="md" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading rewrite history...</p>
        </div>
      )}

      {error && !isLoading && (
        <div className="flex flex-col items-center justify-center py-6">
          <ExclamationTriangleIcon className="h-8 w-8 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      )}

      {history && !isLoading && !error && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary dark:text-primary-light" />
            <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">History</h4>
          </div>

          {/* Render history entries if the API returns an array */}
          {Array.isArray(history) && history.length > 0 ? (
            <div className="space-y-2">
              {history.map((entry, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-[10px] font-bold text-primary dark:text-primary-light">{idx + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="grid grid-cols-2 gap-2 sm:gap-3 text-sm">
                      {entry.originalPowerNumber && (
                        <div>
                          <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Original</span>
                          <p className="font-mono font-medium text-gray-900 dark:text-white">{entry.originalPowerNumber}</p>
                        </div>
                      )}
                      {entry.replacementPowerNumber && (
                        <div>
                          <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Replacement</span>
                          <p className="font-mono font-medium text-gray-900 dark:text-white">{entry.replacementPowerNumber}</p>
                        </div>
                      )}
                      {entry.rewriteDate && (
                        <div>
                          <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date</span>
                          <p className="text-gray-900 dark:text-white">{formatDate(entry.rewriteDate)}</p>
                        </div>
                      )}
                      {entry.rewriteReason && (
                        <div>
                          <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Reason</span>
                          <p className="text-gray-900 dark:text-white">{entry.rewriteReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : Array.isArray(history) && history.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No additional history records found</p>
          ) : (
            /* If history is an object (single record), render its fields */
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700">
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
                {history.originalPowerNumber && <DetailField label="Original Power" value={history.originalPowerNumber} mono />}
                {history.replacementPowerNumber && <DetailField label="Replacement Power" value={history.replacementPowerNumber} mono />}
                {history.rewriteDate && <DetailField label="Rewrite Date" value={formatDate(history.rewriteDate)} />}
                {history.rewriteReason && <DetailField label="Reason" value={history.rewriteReason} />}
                {history.rewriteType && <DetailField label="Type" value={history.rewriteType} />}
                {history.reportId && <DetailField label="Report ID" value={history.reportId} mono />}
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Print-friendly view of all power details
 */
const PrintableContent = ({ power }) => {
  if (!power) return null;

  return (
    <div className="p-8 bg-white text-black print-content">
      {/* Header */}
      <div className="border-b-2 border-gray-300 pb-4 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">Power Details</h1>
            <p className="text-3xl font-mono font-bold mt-1">{power.powerNo}</p>
          </div>
          <div className="text-right">
            <span className={`inline-block px-3 py-1 text-sm font-semibold rounded ${
              power.powerStatus === 'E' ? 'bg-green-100 text-green-800' :
              power.powerStatus === 'D' ? 'bg-blue-100 text-blue-800' :
              power.powerStatus === 'F' ? 'bg-orange-100 text-orange-800' :
              power.powerStatus === 'J' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {power.powerStatusDescription || power.powerStatus}
            </span>
            <p className="text-xs text-gray-500 mt-2">Printed: {new Date().toLocaleString()}</p>
          </div>
        </div>
        {/* Flags */}
        {(power.rewritePowerNo || power.rewriteFromPowerNo || power.missing || (power.expired && power.powerStatus === 'U') || power.wasEverForfeiture || power.wasEverJudgment || power.latestSetAside) && (
          <div className="flex gap-2 mt-3">
            {(power.rewritePowerNo || power.rewriteFromPowerNo) && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">{power.rewriteFromPowerNo ? 'Rewrite' : 'Rewritten'}</span>}
            {power.missing && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">Missing</span>}
            {power.expired && power.powerStatus === 'U' && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">Expired</span>}
            {power.wasEverForfeiture && <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded">Forfeiture History</span>}
            {power.wasEverJudgment && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">Judgment History</span>}
            {power.latestSetAside && <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded">Set-Aside History</span>}
          </div>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Power Information */}
          <div>
            <h2 className="text-lg font-semibold border-b border-gray-200 pb-2 mb-3">Power Information</h2>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div><dt className="text-gray-500">Power Number</dt><dd className="font-mono font-medium">{power.powerNo || '-'}</dd></div>
              <div><dt className="text-gray-500">Power Type</dt><dd className="font-medium">{power.powerTypeDescription || power.powerType || '-'}</dd></div>
              <div><dt className="text-gray-500">Agency</dt><dd className="font-medium">{power.agencyName || '-'}</dd></div>
              <div><dt className="text-gray-500">Executing Agent</dt><dd className="font-medium">{power.executingSubAgentName || '-'}</dd></div>
              <div><dt className="text-gray-500">Batch ID</dt><dd className="font-mono font-medium">{power.batchId || '-'}</dd></div>
              <div><dt className="text-gray-500">Report #</dt><dd className="font-mono font-medium">{power.reportNo || '-'}</dd></div>
            </dl>
            {(power.rewritePowerNo || power.rewriteFromPowerNo) && (
              <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                <p className="text-xs font-semibold text-blue-700 mb-1">Rewrite Information</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {power.rewriteFromPowerNo && <div><span className="text-gray-500">From:</span> <span className="font-mono">{power.rewriteFromPowerNo}</span></div>}
                  {power.rewritePowerNo && <div><span className="text-gray-500">To:</span> <span className="font-mono">{power.rewritePowerNo}</span></div>}
                </div>
              </div>
            )}
          </div>

          {/* Defendant & Case */}
          <div>
            <h2 className="text-lg font-semibold border-b border-gray-200 pb-2 mb-3">Defendant & Case</h2>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="col-span-2"><dt className="text-gray-500">Defendant</dt><dd className="font-medium">{(power.defendantFullName || `${power.defendantFirstName || ''} ${power.defendantLastName || ''}`.trim())?.toUpperCase() || '-'}</dd></div>
              <div><dt className="text-gray-500">Case #</dt><dd className="font-mono font-medium">{power.caseNumber || '-'}</dd></div>
              <div><dt className="text-gray-500">County</dt><dd className="font-medium">{power.countyName || '-'}</dd></div>
              <div><dt className="text-gray-500">City</dt><dd className="font-medium">{power.city?.toUpperCase() || '-'}</dd></div>
              <div><dt className="text-gray-500">State</dt><dd className="font-medium">{power.state || '-'}</dd></div>
            </dl>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Financial Details */}
          <div>
            <h2 className="text-lg font-semibold border-b border-gray-200 pb-2 mb-3">Financial Details</h2>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div><dt className="text-gray-500">Bond Amount</dt><dd className="font-medium">{formatCurrency(power.bondAmount)}</dd></div>
              <div><dt className="text-gray-500">Denomination</dt><dd className="font-medium">{formatCurrency(power.powerAmount)}</dd></div>
              <div><dt className="text-gray-500">Premium Amount</dt><dd className="font-medium">{formatCurrency(power.premiumAmount)}</dd></div>
              <div><dt className="text-gray-500">Net Due</dt><dd className="font-medium">{formatCurrency(power.netDueAmount)}</dd></div>
              <div><dt className="text-gray-500">BUF Amount</dt><dd className="font-medium">{formatCurrency(power.bufAmount)}</dd></div>
              <div><dt className="text-gray-500">Miscellaneous</dt><dd className="font-medium">{formatCurrency(power.miscellaneousAmount)}</dd></div>
            </dl>
          </div>

          {/* Dates */}
          <div>
            <h2 className="text-lg font-semibold border-b border-gray-200 pb-2 mb-3">Important Dates</h2>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div><dt className="text-gray-500">Execution Date</dt><dd className="font-medium">{formatDate(power.executionDate)}</dd></div>
              {power.powerStatus === 'U' && <div><dt className="text-gray-500">Expiration Date</dt><dd className="font-medium">{formatDate(power.expirationDate)}</dd></div>}
              <div><dt className="text-gray-500">Report Date</dt><dd className="font-medium">{formatDate(power.reportDate)}</dd></div>
              <div><dt className="text-gray-500">Assigned Date</dt><dd className="font-medium">{formatDate(power.assignedDate)}</dd></div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Power Detail Modal
 */
const TAB_KEYS = {
  DETAILS: 'details',
  FINANCIAL: 'financial',
  FORFEITURE: 'forfeiture',
  JUDGMENT: 'judgment',
  SET_ASIDE: 'setaside',
  REWRITE: 'rewrite',
  DOCUMENTS: 'documents',
  COMMENTS: 'comments',
};

const PowerDetailModal = ({ power, isOpen, onClose, isLoading, onPowerUpdated }) => {
  const { canAccessContract } = usePermissions();
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Escape key closes directly — capture phase so it runs before Headless UI's handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  // Preserve last power data during close animation to prevent "not found" flash
  const lastPowerRef = useRef(power);

  // Action modal state
  const [isSetAsideOpen, setIsSetAsideOpen] = useState(false);
  const [isDischargeOpen, setIsDischargeOpen] = useState(false);

  // Set-aside pre-validation (check limits before user opens the modal)
  const [setAsideBlocked, setSetAsideBlocked] = useState(false);
  const [setAsideBlockedReason, setSetAsideBlockedReason] = useState('');

  useEffect(() => {
    if (power) {
      lastPowerRef.current = power;
    }
  }, [power]);

  // Use last valid power when closing (power is null but modal still animating out)
  const displayPower = power || (isOpen ? null : lastPowerRef.current);

  // Pre-validate set-aside eligibility when a forfeiture power is loaded
  useEffect(() => {
    if (!isOpen || !displayPower?.powerNo || displayPower.powerStatus !== 'F') {
      setSetAsideBlocked(false);
      setSetAsideBlockedReason('');
      return;
    }
    let cancelled = false;
    validateSetAside(displayPower.powerNo).then((result) => {
      if (cancelled) return;
      if (!result.success || !result.canSetAside) {
        setSetAsideBlocked(true);
        setSetAsideBlockedReason(result.message || 'Set-aside is not available for this power');
      } else {
        setSetAsideBlocked(false);
        setSetAsideBlockedReason('');
      }
    });
    return () => { cancelled = true; };
  }, [isOpen, displayPower?.powerNo, displayPower?.powerStatus]);

  // Tab persistence via URL search params
  const [searchParams, setSearchParams] = useSearchParams();

  // Build ordered list of visible tab keys based on current power data
  const visibleTabs = useMemo(() => {
    const tabs = [TAB_KEYS.DETAILS, TAB_KEYS.FINANCIAL];
    if (displayPower?.latestForfeiture) tabs.push(TAB_KEYS.FORFEITURE);
    if (displayPower?.latestJudgment) tabs.push(TAB_KEYS.JUDGMENT);
    if (displayPower?.latestSetAside) tabs.push(TAB_KEYS.SET_ASIDE);
    if (displayPower?.rewritePowerNo || displayPower?.rewriteFromPowerNo) tabs.push(TAB_KEYS.REWRITE);
    tabs.push(TAB_KEYS.DOCUMENTS, TAB_KEYS.COMMENTS);
    return tabs;
  }, [displayPower]);

  // Resolve URL param to tab index (fall back to 0 if tab not visible)
  const selectedTabIndex = useMemo(() => {
    const tabFromUrl = searchParams.get('detailTab');
    if (!tabFromUrl) return 0;
    const index = visibleTabs.indexOf(tabFromUrl);
    return index >= 0 ? index : 0;
  }, [searchParams, visibleTabs]);

  const handleTabChange = useCallback((index) => {
    const tabKey = visibleTabs[index];
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tabKey && tabKey !== TAB_KEYS.DETAILS) {
        next.set('detailTab', tabKey);
      } else {
        next.delete('detailTab');
      }
      return next;
    }, { replace: true });
  }, [visibleTabs, setSearchParams]);

  // Tab scroll fade indicators
  const tabListRef = useRef(null);
  const [tabScrollFade, setTabScrollFade] = useState({ left: false, right: false });
  const updateTabScrollFade = useCallback(() => {
    const el = tabListRef.current;
    if (!el) return;
    setTabScrollFade({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);
  useEffect(() => {
    const el = tabListRef.current;
    if (!el) return;
    updateTabScrollFade();
    el.addEventListener('scroll', updateTabScrollFade, { passive: true });
    const ro = new ResizeObserver(updateTabScrollFade);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateTabScrollFade); ro.disconnect(); };
  }, [updateTabScrollFade, visibleTabs]);

  // Action modal handlers
  const handleSetAsideSuccess = (updatedPower) => {
    if (updatedPower) lastPowerRef.current = updatedPower;
    onPowerUpdated?.(displayPower?.powerNo);
    setIsSetAsideOpen(false);
  };

  const handleDischargeSuccess = () => {
    onPowerUpdated?.(displayPower?.powerNo);
    setIsDischargeOpen(false);
  };

  // Discharge eligibility (server-driven, with frontend fallback for transfer bonds)
  const isTransferBond = displayPower?.powerStatus === 'T';
  const canShowDischargeButton = displayPower?.canDischarge !== undefined || isTransferBond;
  const isDischargeBlocked = isTransferBond ? false : displayPower?.canDischarge === false;
  const dischargeRequiresProof = isTransferBond
    ? false // Transfer bonds follow the same validation as other bonds — determined at validation time
    : (displayPower?.canDischarge === true && displayPower?.dischargeRequiresProof === true);

  // Handle print functionality
  const handlePrint = () => {
    if (!displayPower) return;

    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      alert('Please allow popups to print');
      return;
    }

    // Get the logo URL
    const logoUrl = `${window.location.origin}/bankers-surety-logo.png`;

    // Build the print content HTML
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Power ${displayPower.powerNo} - Details</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px; color: #111; }
          .logo-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #e5e7eb; }
          .logo { height: 40px; }
          .logo-text { font-size: 11px; color: #666; }
          .header { border-bottom: 2px solid #333; padding-bottom: 16px; margin-bottom: 24px; }
          .header-top { display: flex; justify-content: space-between; align-items: flex-start; }
          .title { font-size: 20px; font-weight: 600; color: #666; }
          .power-no { font-size: 28px; font-weight: 700; font-family: monospace; margin-top: 4px; }
          .status { display: inline-block; padding: 4px 12px; font-size: 12px; font-weight: 600; border-radius: 4px; background: #f3f4f6; }
          .status-E { background: #dcfce7; color: #166534; }
          .status-D { background: #dbeafe; color: #1e40af; }
          .status-F { background: #ffedd5; color: #c2410c; }
          .status-J { background: #fee2e2; color: #dc2626; }
          .print-date { font-size: 11px; color: #666; margin-top: 8px; }
          .flags { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
          .flag { padding: 2px 8px; font-size: 11px; font-weight: 500; border-radius: 4px; }
          .flag-missing { background: #fee2e2; color: #dc2626; }
          .flag-expired { background: #fef3c7; color: #d97706; }
          .flag-forfeiture { background: #ffedd5; color: #c2410c; }
          .flag-judgment { background: #fee2e2; color: #dc2626; }
          .flag-setaside { background: #ccfbf1; color: #0f766e; }
          .columns { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; }
          .section { margin-bottom: 24px; }
          .section-title { font-size: 14px; font-weight: 600; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 12px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; }
          .field { font-size: 12px; }
          .field-label { color: #6b7280; margin-bottom: 2px; }
          .field-value { font-weight: 500; }
          .field-mono { font-family: monospace; }
          .field-full { grid-column: span 2; }
          .flag-rewrite { background: #dbeafe; color: #1d4ed8; }
          @media print {
            body { padding: 0; }
            @page { margin: 0.5in; }
          }
        </style>
      </head>
      <body>
        <div class="logo-header">
          <img src="${logoUrl}" alt="Bankers Surety" class="logo" />
          <div class="logo-text">Printed: ${new Date().toLocaleString()}</div>
        </div>

        <div class="header">
          <div class="header-top">
            <div>
              <div class="title">Power Details</div>
              <div class="power-no">${displayPower.powerNo}</div>
            </div>
            <div style="text-align: right;">
              <span class="status status-${displayPower.powerStatus}">${displayPower.powerStatusDescription || displayPower.powerStatus}</span>
            </div>
          </div>
          ${(displayPower.rewritePowerNo || displayPower.rewriteFromPowerNo || displayPower.missing || (displayPower.expired && displayPower.powerStatus === 'U') || displayPower.wasEverForfeiture || displayPower.wasEverJudgment || displayPower.latestSetAside) ? `
          <div class="flags">
            ${(displayPower.rewritePowerNo || displayPower.rewriteFromPowerNo) ? `<span class="flag flag-rewrite">${displayPower.rewriteFromPowerNo ? 'Rewrite' : 'Rewritten'}</span>` : ''}
            ${displayPower.missing ? '<span class="flag flag-missing">Missing</span>' : ''}
            ${displayPower.expired && displayPower.powerStatus === 'U' ? '<span class="flag flag-expired">Expired</span>' : ''}
            ${displayPower.wasEverForfeiture ? '<span class="flag flag-forfeiture">Forfeiture History</span>' : ''}
            ${displayPower.wasEverJudgment ? '<span class="flag flag-judgment">Judgment History</span>' : ''}
            ${displayPower.latestSetAside ? '<span class="flag flag-setaside">Set-Aside History</span>' : ''}
          </div>
          ` : ''}
        </div>

        <div class="columns">
          <div>
            <div class="section">
              <div class="section-title">Power Information</div>
              <div class="grid">
                <div class="field"><div class="field-label">Power Number</div><div class="field-value field-mono">${displayPower.powerNo || '-'}</div></div>
                <div class="field"><div class="field-label">Power Type</div><div class="field-value">${displayPower.powerTypeDescription || displayPower.powerType || '-'}</div></div>
                <div class="field"><div class="field-label">Agency</div><div class="field-value">${displayPower.agencyName || '-'}</div></div>
                <div class="field"><div class="field-label">Executing Agent</div><div class="field-value">${displayPower.executingSubAgentName || '-'}</div></div>
                <div class="field"><div class="field-label">Batch ID</div><div class="field-value field-mono">${displayPower.batchId || '-'}</div></div>
                <div class="field"><div class="field-label">Report #</div><div class="field-value field-mono">${displayPower.reportNo || '-'}</div></div>
              </div>
              ${(displayPower.rewritePowerNo || displayPower.rewriteFromPowerNo) ? `
              <div style="margin-top: 12px; padding: 8px 12px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px;">
                <div style="font-size: 11px; font-weight: 600; color: #1d4ed8; margin-bottom: 6px;">Rewrite Information</div>
                <div class="grid">
                  ${displayPower.rewriteFromPowerNo ? `<div class="field"><div class="field-label">Original Power</div><div class="field-value field-mono">${displayPower.rewriteFromPowerNo}</div></div>` : ''}
                  ${displayPower.rewritePowerNo ? `<div class="field"><div class="field-label">Rewrite Power</div><div class="field-value field-mono">${displayPower.rewritePowerNo}</div></div>` : ''}
                </div>
              </div>
              ` : ''}
            </div>

            <div class="section">
              <div class="section-title">Defendant & Case</div>
              <div class="grid">
                <div class="field field-full"><div class="field-label">Defendant</div><div class="field-value">${(displayPower.defendantFullName || ((displayPower.defendantFirstName || '') + ' ' + (displayPower.defendantLastName || '')).trim())?.toUpperCase() || '-'}</div></div>
                <div class="field"><div class="field-label">Case #</div><div class="field-value field-mono">${displayPower.caseNumber || '-'}</div></div>
                <div class="field"><div class="field-label">County</div><div class="field-value">${displayPower.countyName || '-'}</div></div>
                <div class="field"><div class="field-label">City</div><div class="field-value">${displayPower.city?.toUpperCase() || '-'}</div></div>
                <div class="field"><div class="field-label">State</div><div class="field-value">${displayPower.state || '-'}</div></div>
              </div>
            </div>
          </div>

          <div>
            <div class="section">
              <div class="section-title">Financial Details</div>
              <div class="grid">
                <div class="field"><div class="field-label">Bond Amount</div><div class="field-value">${formatCurrency(displayPower.bondAmount)}</div></div>
                <div class="field"><div class="field-label">Denomination</div><div class="field-value">${formatCurrency(displayPower.powerAmount)}</div></div>
                <div class="field"><div class="field-label">Premium Amount</div><div class="field-value">${formatCurrency(displayPower.premiumAmount)}</div></div>
                <div class="field"><div class="field-label">Net Due</div><div class="field-value">${formatCurrency(displayPower.netDueAmount)}</div></div>
                <div class="field"><div class="field-label">BUF Amount</div><div class="field-value">${formatCurrency(displayPower.bufAmount)}</div></div>
                <div class="field"><div class="field-label">Miscellaneous</div><div class="field-value">${formatCurrency(displayPower.miscellaneousAmount)}</div></div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Important Dates</div>
              <div class="grid">
                <div class="field"><div class="field-label">Execution Date</div><div class="field-value">${formatDate(displayPower.executionDate)}</div></div>
                ${displayPower.powerStatus === 'U' ? `<div class="field"><div class="field-label">Expiration Date</div><div class="field-value">${formatDate(displayPower.expirationDate)}</div></div>` : ''}
                <div class="field"><div class="field-label">Report Date</div><div class="field-value">${formatDate(displayPower.reportDate)}</div></div>
                <div class="field"><div class="field-label">Assigned Date</div><div class="field-value">${formatDate(displayPower.assignedDate)}</div></div>
              </div>
            </div>
          </div>
        </div>

        ${displayPower.latestForfeiture ? `
        <div class="section">
          <div class="section-title">Forfeiture</div>
          <div class="grid">
            <div class="field"><div class="field-label">Forfeiture Amount</div><div class="field-value">${formatCurrency(displayPower.latestForfeiture.forfeitureAmount)}</div></div>
            <div class="field"><div class="field-label">Forfeiture Date</div><div class="field-value">${formatDate(displayPower.latestForfeiture.forfeitureDate)}</div></div>
            <div class="field"><div class="field-label">Due Date</div><div class="field-value">${formatDate(displayPower.latestForfeiture.dueDate)}</div></div>
            <div class="field"><div class="field-label">Extension Date</div><div class="field-value">${formatDate(displayPower.latestForfeiture.extensionDate)}</div></div>
            ${displayPower.latestForfeiture.judgementDate ? `
            <div class="field"><div class="field-label">Judgment Date</div><div class="field-value">${formatDate(displayPower.latestForfeiture.judgementDate)}</div></div>
            <div class="field"><div class="field-label">Judgment Amount</div><div class="field-value">${formatCurrency(displayPower.latestForfeiture.judgementAmount)}</div></div>
            ` : ''}
            ${displayPower.latestForfeiture.setAsideDate ? `
            <div class="field"><div class="field-label">Set Aside Date</div><div class="field-value">${formatDate(displayPower.latestForfeiture.setAsideDate)}</div></div>
            ` : ''}
            ${displayPower.latestForfeiture.dischargeDate ? `
            <div class="field"><div class="field-label">Discharge Date</div><div class="field-value">${formatDate(displayPower.latestForfeiture.dischargeDate)}</div></div>
            ` : ''}
          </div>
        </div>
        ` : ''}

        ${displayPower.latestJudgment ? `
        <div class="section">
          <div class="section-title">Judgment</div>
          <div class="grid">
            <div class="field"><div class="field-label">Judgment Amount</div><div class="field-value">${formatCurrency(displayPower.latestJudgment.judgmentAmount)}</div></div>
            <div class="field"><div class="field-label">Judgment Date</div><div class="field-value">${formatDate(displayPower.latestJudgment.judgmentDate)}</div></div>
            <div class="field"><div class="field-label">Due Date</div><div class="field-value">${formatDate(displayPower.latestJudgment.judgmentDueDate)}</div></div>
            ${displayPower.latestJudgment.duePayableAmount ? `
            <div class="field"><div class="field-label">Due Payable Amount</div><div class="field-value">${formatCurrency(displayPower.latestJudgment.duePayableAmount)}</div></div>
            ` : ''}
            ${displayPower.latestJudgment.duePayableDate ? `
            <div class="field"><div class="field-label">Due Payable Date</div><div class="field-value">${formatDate(displayPower.latestJudgment.duePayableDate)}</div></div>
            ` : ''}
            ${displayPower.latestJudgment.dischargeDate ? `
            <div class="field"><div class="field-label">Discharge Date</div><div class="field-value">${formatDate(displayPower.latestJudgment.dischargeDate)}</div></div>
            ` : ''}
          </div>
        </div>
        ` : ''}

        ${displayPower.latestSetAside ? `
        <div class="section">
          <div class="section-title">Set-Aside</div>
          <div class="grid">
            <div class="field"><div class="field-label">Set-Aside Date</div><div class="field-value">${formatDate(displayPower.latestSetAside.setAsideDate)}</div></div>
            <div class="field"><div class="field-label">Reason</div><div class="field-value">${displayPower.latestSetAside.setAsideReasonDescription || displayPower.latestSetAside.setAsideReasonCd || '-'}</div></div>
            ${displayPower.latestSetAside.setAsideReasonOther ? `
            <div class="field field-full"><div class="field-label">Other Reason</div><div class="field-value">${displayPower.latestSetAside.setAsideReasonOther}</div></div>
            ` : ''}
          </div>
        </div>
        ` : ''}
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load, then print
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  return (
    <>
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setShowCloseConfirm(true)}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          {/* Mobile: full screen from bottom, Desktop: centered modal */}
          <div className="flex min-h-full items-end sm:items-center justify-center sm:p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="w-full max-h-[95vh] sm:max-h-none sm:max-w-3xl transform overflow-hidden overflow-x-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 shadow-xl transition-all flex flex-col">
                {/* Header */}
                <div className="flex-shrink-0 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border-b border-gray-200 dark:border-gray-700">
                  {/* Mobile drag handle */}
                  <div className="sm:hidden flex justify-center mb-1.5">
                    <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap">
                        <DialogTitle className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Power
                        </DialogTitle>
                        {displayPower && (
                          <p className="text-base sm:text-xl font-bold font-mono text-gray-900 dark:text-white tracking-tight">
                            {displayPower.powerNo}
                          </p>
                        )}
                        {displayPower && (
                          <span className={`px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-xs font-semibold rounded-md ${getPowerStatusColor(displayPower.powerStatus)}`}>
                            {displayPower.powerStatusDescription || displayPower.powerStatus}
                          </span>
                        )}
                      </div>
                      {displayPower && (
                        <>
                          {(displayPower.rewritePowerNo || displayPower.rewriteFromPowerNo || displayPower.missing || (displayPower.expired && displayPower.powerStatus === 'U') || displayPower.wasEverForfeiture || displayPower.wasEverJudgment || displayPower.latestSetAside) && (
                            <div className="flex items-center gap-1 sm:gap-1.5 mt-1 sm:mt-1.5 flex-wrap">
                              {(displayPower.rewritePowerNo || displayPower.rewriteFromPowerNo) && (
                                <span className="px-1.5 sm:px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[10px] sm:text-xs font-medium rounded-md border border-blue-200 dark:border-blue-800">
                                  {displayPower.rewriteFromPowerNo === displayPower.powerNo
                                    ? <>Rewritten to <span className="font-mono">{displayPower.rewritePowerNo}</span></>
                                    : <>Rewrite of <span className="font-mono">{displayPower.rewriteFromPowerNo}</span></>}
                                </span>
                              )}
                              {displayPower.missing && (
                                <span className="px-1.5 sm:px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-[10px] sm:text-xs font-medium rounded-md border border-red-200 dark:border-red-800">Missing</span>
                              )}
                              {displayPower.expired && displayPower.powerStatus === 'U' && (
                                <span className="px-1.5 sm:px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] sm:text-xs font-medium rounded-md border border-amber-200 dark:border-amber-800">Expired</span>
                              )}
                              {displayPower.wasEverForfeiture && (
                                <span className="px-1.5 sm:px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-[10px] sm:text-xs font-medium rounded-md border border-orange-200 dark:border-orange-800">Forfeiture History</span>
                              )}
                              {displayPower.wasEverJudgment && (
                                <span className="px-1.5 sm:px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-[10px] sm:text-xs font-medium rounded-md border border-red-200 dark:border-red-800">Judgment History</span>
                              )}
                              {displayPower.latestSetAside && (
                                <span className="px-1.5 sm:px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-[10px] sm:text-xs font-medium rounded-md border border-teal-200 dark:border-teal-800">Set-Aside History</span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <button
                      onClick={onClose}
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors flex-shrink-0"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 px-4 sm:px-6 py-3 sm:py-4 overflow-y-auto">
                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-8 sm:py-12">
                      <OathSpinner size="lg" />
                      <p className="mt-3 sm:mt-4 text-sm text-gray-500 dark:text-gray-400">Loading power details...</p>
                    </div>
                  ) : !displayPower ? (
                    <div className="flex flex-col items-center justify-center py-8 sm:py-12">
                      <ExclamationTriangleIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                      <p className="mt-3 sm:mt-4 text-sm text-gray-500 dark:text-gray-400">Power details not found</p>
                    </div>
                  ) : (
                    <TabGroup selectedIndex={selectedTabIndex} onChange={handleTabChange}>
                      <div className="relative mb-3 sm:mb-4 -mx-4 sm:mx-0">
                      {tabScrollFade.left && <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white dark:from-gray-800 to-transparent z-10 pointer-events-none sm:hidden" />}
                      {tabScrollFade.right && <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white dark:from-gray-800 to-transparent z-10 pointer-events-none sm:hidden" />}
                      <TabList ref={tabListRef} className="flex gap-1 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-0 overflow-x-auto scrollbar-hide">
                        <Tab className={({ selected }) =>
                          `px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-t-lg transition-colors outline-none whitespace-nowrap flex-shrink-0 ${
                            selected
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light border-b-2 border-primary'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`
                        }>
                          Details
                        </Tab>
                        {canAccessContract && (
                          <Tab className={({ selected }) =>
                            `px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-t-lg transition-colors outline-none whitespace-nowrap flex-shrink-0 ${
                              selected
                                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light border-b-2 border-primary'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`
                          }>
                            Financial
                          </Tab>
                        )}
                        {/* Conditional Forfeiture Tab */}
                        {displayPower.latestForfeiture && (
                          <Tab className={({ selected }) =>
                            `px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-t-lg transition-colors outline-none whitespace-nowrap flex-shrink-0 ${
                              selected
                                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light border-b-2 border-primary'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`
                          }>
                            Forfeiture
                          </Tab>
                        )}
                        {/* Conditional Judgment Tab */}
                        {displayPower.latestJudgment && (
                          <Tab className={({ selected }) =>
                            `px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-t-lg transition-colors outline-none whitespace-nowrap flex-shrink-0 ${
                              selected
                                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light border-b-2 border-primary'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`
                          }>
                            Judgment
                          </Tab>
                        )}
                        {/* Conditional Set-Aside Tab */}
                        {displayPower.latestSetAside && (
                          <Tab className={({ selected }) =>
                            `px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-t-lg transition-colors outline-none whitespace-nowrap flex-shrink-0 ${
                              selected
                                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light border-b-2 border-primary'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`
                          }>
                            Set-Aside
                          </Tab>
                        )}
                        {/* Conditional Rewrite Tab */}
                        {(displayPower.rewritePowerNo || displayPower.rewriteFromPowerNo) && (
                          <Tab className={({ selected }) =>
                            `px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-t-lg transition-colors outline-none whitespace-nowrap flex-shrink-0 ${
                              selected
                                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light border-b-2 border-primary'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`
                          }>
                            Rewrite
                          </Tab>
                        )}
                        {/* Documents Tab */}
                        <Tab className={({ selected }) =>
                          `px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-t-lg transition-colors outline-none whitespace-nowrap flex-shrink-0 ${
                            selected
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light border-b-2 border-primary'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`
                        }>
                          Documents
                        </Tab>
                        {/* Comments Tab - Always last */}
                        <Tab className={({ selected }) =>
                          `px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-t-lg transition-colors outline-none whitespace-nowrap flex-shrink-0 ${
                            selected
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light border-b-2 border-primary'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`
                        }>
                          Comments
                        </Tab>
                      </TabList>
                      </div>

                      <TabPanels>
                        {/* Details Tab - Combined Power Info, Case, and Dates */}
                        <TabPanel className="space-y-3 sm:space-y-4">
                          <Section icon={DocumentTextIcon} title="Power Information">
                            <DetailField label="Power Number" value={displayPower.powerNo} mono />
                            <DetailField label="Power Type" value={displayPower.powerTypeDescription || displayPower.powerType} />
                            <DetailField label="Agency" value={displayPower.agencyName} />
                            <DetailField label="Executing Agent" value={displayPower.executingSubAgentName} />
                            <DetailField label="Batch ID" value={displayPower.batchId} mono />
                            <DetailField label="Report #" value={displayPower.reportNo} mono />
                          </Section>

                          <Section icon={UserIcon} title="Defendant & Case">
                            <DetailField label="Defendant" value={(displayPower.defendantFullName || `${displayPower.defendantFirstName || ''} ${displayPower.defendantLastName || ''}`.trim())?.toUpperCase()} />
                            <DetailField label="Case #" value={displayPower.caseNumber} mono />
                            <DetailField label="County" value={displayPower.countyName} />
                            <DetailField label="City" value={displayPower.city?.toUpperCase()} />
                            <DetailField label="State" value={displayPower.state} />
                          </Section>

                          <Section icon={CalendarIcon} title="Dates">
                            <DetailField label="Execution" value={formatDate(displayPower.executionDate)} />
                            {displayPower.powerStatus === 'U' && <DetailField label="Expiration" value={formatDate(displayPower.expirationDate)} />}
                            <DetailField label="Report" value={formatDate(displayPower.reportDate)} />
                            <DetailField label="Assigned" value={formatDate(displayPower.assignedDate)} />
                          </Section>
                        </TabPanel>

                        {/* Financial Tab */}
                        {canAccessContract && (
                          <TabPanel>
                            <Section icon={CurrencyDollarIcon} title="Financial Details">
                              <DetailField label="Bond Amount" value={formatCurrency(displayPower.bondAmount)} />
                              <DetailField label="Denomination" value={formatCurrency(displayPower.powerAmount)} />
                              <DetailField label="Premium Amount" value={formatCurrency(displayPower.premiumAmount)} />
                              <DetailField label="Net Due" value={formatCurrency(displayPower.netDueAmount)} />
                              <DetailField label="BUF Amount" value={formatCurrency(displayPower.bufAmount)} />
                              <DetailField label="Miscellaneous" value={formatCurrency(displayPower.miscellaneousAmount)} />
                            </Section>
                          </TabPanel>
                        )}

                        {/* Conditional Forfeiture Tab Panel */}
                        {displayPower.latestForfeiture && (
                          <TabPanel>
                            <ForfeitureSection forfeiture={displayPower.latestForfeiture} />
                          </TabPanel>
                        )}

                        {/* Conditional Judgment Tab Panel */}
                        {displayPower.latestJudgment && (
                          <TabPanel>
                            <JudgmentSection judgment={displayPower.latestJudgment} />
                          </TabPanel>
                        )}

                        {/* Conditional Set-Aside Tab Panel */}
                        {displayPower.latestSetAside && (
                          <TabPanel>
                            <SetAsideSection setAside={displayPower.latestSetAside} />
                          </TabPanel>
                        )}

                        {/* Conditional Rewrite Tab Panel */}
                        {(displayPower.rewritePowerNo || displayPower.rewriteFromPowerNo) && (
                          <TabPanel>
                            <RewriteHistorySection power={displayPower} />
                          </TabPanel>
                        )}

                        {/* Documents Tab */}
                        <TabPanel>
                          <DocumentsSection powerNo={displayPower.powerNo} initialDocId={searchParams.get('doc')} />
                        </TabPanel>

                        {/* Comments Tab - Always last */}
                        <TabPanel>
                          <CommentsSection powerNo={displayPower.powerNo} />
                        </TabPanel>
                      </TabPanels>
                    </TabGroup>
                  )}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                  {/* Action buttons row - only shown when at least one action is available */}
                  {displayPower && (displayPower.powerStatus === 'F' || canShowDischargeButton) && (
                    <div className="px-4 sm:px-6 py-2.5 sm:py-3 flex items-center gap-2 border-b border-gray-200/60 dark:border-gray-700/60">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mr-1 hidden sm:inline">Actions</span>
                      {displayPower.powerStatus === 'F' && (
                        <Tooltip
                          title={setAsideBlocked ? setAsideBlockedReason : ''}
                          placement="top"
                          arrow
                        >
                          <span>
                            <button
                              onClick={() => !setAsideBlocked && setIsSetAsideOpen(true)}
                              disabled={setAsideBlocked}
                              className={`group flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg border transition-all ${
                                setAsideBlocked
                                  ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60'
                                  : 'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/40 hover:border-orange-300 dark:hover:border-orange-700 active:scale-[0.97]'
                              }`}
                            >
                              <NoSymbolIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                              Set-Aside
                            </button>
                          </span>
                        </Tooltip>
                      )}
                      {canShowDischargeButton && (
                        <Tooltip
                          title={isDischargeBlocked ? (displayPower.dischargeBlockedReason || 'Discharge is not available') : ''}
                          placement="top"
                          arrow
                        >
                          <span>
                            <button
                              onClick={() => !isDischargeBlocked && setIsDischargeOpen(true)}
                              disabled={isDischargeBlocked}
                              className={`group flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg border transition-all ${
                                isDischargeBlocked
                                  ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60'
                                  : 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:border-blue-300 dark:hover:border-blue-700 active:scale-[0.97]'
                              }`}
                            >
                              <DocumentMinusIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                              Discharge
                            </button>
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  )}
                  {/* Utility buttons row */}
                  <div className="px-4 sm:px-6 py-2.5 sm:py-3 flex justify-between items-center">
                    <button
                      onClick={handlePrint}
                      disabled={isLoading || !displayPower}
                      className="flex items-center gap-2 px-3 py-1.5 text-gray-500 dark:text-gray-400 text-sm font-medium rounded-lg hover:bg-gray-200/60 dark:hover:bg-gray-700/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PrinterIcon className="h-4 w-4" />
                      Print
                    </button>
                    <button
                      onClick={onClose}
                      className="px-4 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* Action modals - portaled to body at z-[60] so they render above the z-50 Dialog */}
    {(isSetAsideOpen || isDischargeOpen) && createPortal(
      <div className="relative z-[60]">
        <SetAsideModal
          isOpen={isSetAsideOpen}
          onClose={() => setIsSetAsideOpen(false)}
          power={displayPower}
          onSuccess={handleSetAsideSuccess}
        />
        <DischargeModal
          isOpen={isDischargeOpen}
          onClose={() => setIsDischargeOpen(false)}
          power={displayPower}
          onSuccess={handleDischargeSuccess}
          agencyId={displayPower?.agencyId || null}
        />
      </div>,
      document.body
    )}

    <OathConfirmDialog
      open={showCloseConfirm}
      title="Close this window?"
      message="Any unsaved changes will be lost."
      confirmLabel="Close"
      cancelLabel="Go Back"
      variant="warning"
      onConfirm={() => { setShowCloseConfirm(false); onClose(); }}
      onCancel={() => setShowCloseConfirm(false)}
    />
  </>
  );
};

export default PowerDetailModal;
